import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type Props = {
  /** 見出しアイコン（薄いトーンで表示） */
  icon: React.ReactNode;
  /** 主メッセージ */
  title: string;
  /** 補足説明（任意） */
  description?: string;
  /** 次アクション（任意。ボタンとして左寄せで表示） */
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
};

/**
 * 記事系の空表示（Empty State）。
 * 「リストが空／検索結果ゼロ」向けに、コンテンツ領域の起点と揃うよう左寄せで表示する。
 * アイコンはトーンを落とし、可能なら次アクションを添えて行き止まりにしない。
 * （エラー・ローディングは中央寄せのままにするため、この共通コンポーネントは使わない）
 */
const ArticleEmptyState: React.FC<Props> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionIcon,
}) => {
  return (
    <Box
      sx={{
        py: 5,
        px: 0.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 480 }}>
          {description}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Stack direction="row" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={actionIcon}
            onClick={onAction}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {actionLabel}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default ArticleEmptyState;
